import {
  useEffect,
  useState,
  useMemo,
  useCallback,
  useRef,
  lazy,
  Suspense,
  type ChangeEvent,
} from "react";
import {
  Form,
  Dropdown,
  Nav,
  Offcanvas,
  Button,
  InputGroup,
  Badge,
} from "react-bootstrap";
import { BsFilter, BsPeople, BsEnvelopeArrowDown, BsSearch, BsSortDown } from "react-icons/bs";
import { useFindUsers } from "../../api/queries/useFindUser";
import { User, UserImageUrls } from "../../types/Types";
import { useGetConnectionRequests } from "../../api/queries/useGetConnectionRequests";
import { useGetSentConnectionRequests } from "../../api/queries/useGetSentConnectionsRequests";
import { useGetConnections } from "../../api/queries/useGetConnections";
import { useAppContext } from "../../Context/AppContext";
import { countries } from "countries-list";
import { formatCountryForLocale, getCountryFlagUrl } from "../../utils/countryFlag";
import { useTranslation } from "react-i18next";
import { useTranslateDynamicFields } from "../../api/queries/useTranslateDynamicFields";
import { useLiveTranslatedInput } from "../../hooks/useLiveTranslatedInput";

const UsersList = lazy(() => import("./UsersList"));
const ConnectionsList = lazy(() => import("./ConnectionList"));
const RequestsList = lazy(() => import("./RequestsList"));

/** Values are sent to the API unchanged; `labelKey` is for `t()` display only. */
const SECTOR_FILTER_OPTIONS = [
  { value: "Accounting and Finance", labelKey: "connections.sectors.accountingFinance" },
  { value: "Technology & IT", labelKey: "connections.sectors.technologyIt" },
  { value: "Retail & E-Commerce", labelKey: "connections.sectors.retailEcommerce" },
  { value: "Healthcare & Medical", labelKey: "connections.sectors.healthcareMedical" },
  { value: "Education & Training", labelKey: "connections.sectors.educationTraining" },
  { value: "Other", labelKey: "connections.sectors.other" },
] as const;

const TURNOVER_FILTER_OPTIONS = [
  { value: "0-100K", labelKey: "connections.turnover.0_100K" },
  { value: "100K-500K", labelKey: "connections.turnover.100K_500K" },
  { value: "500K-1M", labelKey: "connections.turnover.500K_1M" },
  { value: "1M-5M", labelKey: "connections.turnover.1M_5M" },
  { value: "5M-10M", labelKey: "connections.turnover.5M_10M" },
  { value: "10M+", labelKey: "connections.turnover.10M_plus" },
] as const;

const COUNTRY_OPTIONS = Object.freeze(
  Object.values(countries)
    .map((c) => c.name)
    .sort()
);

/**
 * City filter dropdown: only these names (exact casing) may appear — matches product reference list.
 * Cities present in user data but not listed here are hidden; order follows this list.
 */
const CONNECTION_CITY_FILTER_ALLOWLIST = Object.freeze([
  "Birmingham",
  "Bradford",
  "Earley, Reading",
  "haripur",
  "Haripur",
  "Leicester",
  "Liverpool",
  "London",
  "Manchester",
  "Newcastle",
  "Norwich",
  "Sale, Manchester",
  "SINGAPORE",
] as const);

const CONNECTION_CITY_FILTER_SET = new Set<string>(CONNECTION_CITY_FILTER_ALLOWLIST);

/** OR within each dimension, AND across dimensions — matches /User/All discover filters. */
function userMatchesDiscoverFilters(
  u: User,
  opts: { cities: string[]; sectors: string[]; turnovers: string[]; countries: string[] }
): boolean {
  const { cities, sectors, turnovers, countries } = opts;
  if (cities.length > 0) {
    const city = (u.city ?? "").trim();
    if (!city) return false;
    if (!cities.some((c) => city.toLowerCase() === c.trim().toLowerCase())) return false;
  }
  if (sectors.length > 0) {
    const s = (u.businessSectorIndustry ?? "").trim();
    if (!s) return false;
    if (!sectors.some((x) => s.toLowerCase() === x.trim().toLowerCase())) return false;
  }
  if (turnovers.length > 0) {
    const t = (u.businessTurnover ?? "").trim();
    if (!t) return false;
    if (!turnovers.some((x) => t.toLowerCase() === x.trim().toLowerCase())) return false;
  }
  if (countries.length > 0) {
    const c0 = (u.businessCountryOperation ?? "").trim();
    if (!c0) return false;
    if (!countries.some((x) => c0.toLowerCase() === x.trim().toLowerCase())) return false;
  }
  return true;
}

/** Pure helper: upgrade message for filter based on membership and filter type. */
function getUpgradeMessageKey(
  membershipType: string,
  filterType: string
): string {
  if (membershipType === "youth") {
    return "connections.upgrade.businessOrExecutive";
  }
  if (membershipType === "business") {
    if (filterType === "turnover" || filterType === "country") {
      return "connections.upgrade.executiveOnly";
    }
  }
  return "";
}

// Styles moved to CSS classes

const BODY_CLASS_MOBILE_SCROLL_LOCK = "connections-mobile-noscroll";

function Connections() {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? i18n.language;
  const { objectId, user: currentUser } = useAppContext();
  const userId = objectId ?? "";

  /* Lock body scroll on mobile so only the inner scroll area scrolls; desktop unchanged */
  useEffect(() => {
    const applyLock = () => {
      if (typeof window !== "undefined" && window.matchMedia("(max-width: 767px)").matches) {
        document.body.classList.add(BODY_CLASS_MOBILE_SCROLL_LOCK);
      }
    };
    const removeLock = () => document.body.classList.remove(BODY_CLASS_MOBILE_SCROLL_LOCK);
    const handleChange = (e: MediaQueryListEvent) => {
      if (e.matches) applyLock();
      else removeLock();
    };
    applyLock();
    const mql = window.matchMedia("(max-width: 767px)");
    mql.addEventListener("change", handleChange);
    return () => {
      removeLock();
      mql.removeEventListener("change", handleChange);
    };
  }, []);

  /** Shown in the search input (may be live-translated). */
  const [searchDisplay, setSearchDisplay] = useState("");
  /** Raw query for API + list filters — not overwritten by translation. */
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [sortOption, setSortOption] = useState("connections.sort.recent");
  const [hasMoreUsers, setHasMoreUsers] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);

  const [fullUsersList, setFullUsersList] = useState<User[]>([]);
  const [fullUsersImages, setFullUsersImages] = useState<UserImageUrls[]>([]);

  // Filter states
  const [appliedFilters, setAppliedFilters] = useState({
    cities: [] as string[],
    sectors: [] as string[],
    turnovers: [] as string[],
    countries: [] as string[],
  });

  // Filter input states
  const [cityFilter, setCityFilter] = useState("");
  const [sectorFilter, setSectorFilter] = useState("");
  const [turnoverFilter, setTurnoverFilter] = useState("");
  const [countryFilter, setCountryFilter] = useState("");

  const cityOptions = useMemo(
    () => [...CONNECTION_CITY_FILTER_ALLOWLIST],
    []
  );
  const cityTranslationFields = useMemo(
    () =>
      cityOptions.reduce<Record<string, string>>((acc, city, index) => {
        acc[`city_${index}`] = city;
        return acc;
      }, {}),
    [cityOptions]
  );
  const { data: translatedCities } = useTranslateDynamicFields(
    cityTranslationFields,
    i18n.resolvedLanguage || i18n.language
  );
  const translatedCityByValue = useMemo(() => {
    const map = new Map<string, string>();
    cityOptions.forEach((city, index) => {
      const translated = translatedCities?.[`city_${index}`]?.trim();
      map.set(city, translated || city);
    });
    return map;
  }, [cityOptions, translatedCities]);
  const displayCity = useCallback(
    (city: string) => translatedCityByValue.get(city) || city,
    [translatedCityByValue]
  );

  const [showFilters, setShowFilters] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const searchDisplayRef = useRef("");
  const {
    bindLiveText: bindConnectionsSearchLive,
    retranslateCurrent: retranslateConnectionsSearch,
    isTranslating: isConnectionsSearchTranslating,
    messageTargetLang: connectionsSearchLiveLang,
  } = useLiveTranslatedInput();

  searchDisplayRef.current = searchDisplay;

  useEffect(() => {
    retranslateConnectionsSearch(
      () => searchDisplayRef.current,
      setSearchDisplay
    );
  }, [connectionsSearchLiveLang, retranslateConnectionsSearch]);

  const handleConnectionsSearchChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const raw = e.target.value;
      setSearchQuery(raw);
      bindConnectionsSearchLive(raw, setSearchDisplay);
    },
    [bindConnectionsSearchLive]
  );

  const handleConnectionsSearchBlur = useCallback(() => {
    retranslateConnectionsSearch(
      () => searchDisplayRef.current,
      setSearchDisplay
    );
  }, [retranslateConnectionsSearch]);

  const [activeTab, setActiveTab] = useState<
    "connections" | "requests" | "users"
  >("users");
  /* ================= MEMBERSHIP HELPERS ================= */

  const membershipType = useMemo(() => {
    const membership = currentUser?.user?.membershipName?.toLowerCase() || "";
    if (membership === "youth membership") return "youth";
    if (membership === "business membership") return "business";
    if (membership === "executive membership") return "executive";
    return "youth";
  }, [currentUser?.user?.membershipName]);
  const canUseCityFilter = membershipType === "business" || membershipType === "executive";
  const canUseSectorFilter = membershipType === "business" || membershipType === "executive";
  const canUseTurnoverFilter = membershipType === "executive";
  const canUseCountryFilter = membershipType === "executive";

  /* ================= API ================= */

  const { data: userResponse, refetch: refetchUsers } = useFindUsers({
    pageSize: 50,
    page: currentPage,
    searchTerm: debouncedSearchTerm || undefined,
    discoverableOnly: true,
    cities: canUseCityFilter && appliedFilters.cities.length > 0 ? appliedFilters.cities : undefined,
    sectors: canUseSectorFilter && appliedFilters.sectors.length > 0 ? appliedFilters.sectors : undefined,
    turnovers: canUseTurnoverFilter && appliedFilters.turnovers.length > 0 ? appliedFilters.turnovers : undefined,
    countries: canUseCountryFilter && appliedFilters.countries.length > 0 ? appliedFilters.countries : undefined,
  });

  const { data: connectionRequests, refetch: refetchConnectionRequests } =
    useGetConnectionRequests({ userId });

  const { data: sentConnectionRequests, refetch: refetchSentConnectionRequests } =
    useGetSentConnectionRequests({ userId });

  const { data: connections, refetch: refetchConnections } =
    useGetConnections({ userId });

  /* ================= EFFECTS ================= */

  // Drop city filters not in the fixed allowlist (e.g. legacy state).
  useEffect(() => {
    setAppliedFilters((prev) => {
      const cities = prev.cities.filter((c) => CONNECTION_CITY_FILTER_SET.has(c));
      if (cities.length === prev.cities.length) return prev;
      return { ...prev, cities };
    });
  }, []);

  // Reset pagination and clear users list when search or filters change
  useEffect(() => {
    setCurrentPage(1);
    setHasMoreUsers(true);
    // Clear the users list when filters change
    setFullUsersList([]);
    setFullUsersImages([]);
  }, [debouncedSearchTerm, appliedFilters]);

  useEffect(() => {
    if (userResponse) {
      const newUsers = userResponse?.users?.data ?? [];
      const newImages = userResponse?.userImageUrls ?? [];

      // If it's the first page, replace the list; otherwise append (avoid duplicates)
      if (currentPage === 1) {
        setFullUsersList(newUsers);
        setFullUsersImages(newImages);
      } else {
        // Append only new users (avoid duplicates by checking id)
        setFullUsersList((p) => {
          const existingIds = new Set(p.map((u) => u.id));
          const uniqueNewUsers = newUsers.filter((u) => !existingIds.has(u.id));
          return [...p, ...uniqueNewUsers];
        });
        setFullUsersImages((p) => {
          const existingIds = new Set(p.map((img) => img.userId));
          const uniqueNewImages = newImages.filter(
            (img) => !existingIds.has(img.userId)
          );
          return [...p, ...uniqueNewImages];
        });
      }

      // Update hasMoreUsers based on totalPages
      const totalPages = userResponse?.users?.totalPages ?? 0;
      const currentPageNum = userResponse?.users?.pageNumber ?? currentPage;
      setHasMoreUsers(currentPageNum < totalPages);
    }
  }, [userResponse, currentPage]);

  // Refetch list when switching tabs
  useEffect(() => {
    if (activeTab === "connections") refetchConnections();
    if (activeTab === "requests") refetchConnectionRequests();
    if (activeTab === "users") refetchUsers();
  }, [activeTab, refetchConnections, refetchConnectionRequests, refetchUsers]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchQuery);
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [searchQuery]);

  /* ================= FILTER FUNCTIONS ================= */

  const addFilter = useCallback(
    (key: keyof typeof appliedFilters, value: string) => {
      if (!value.trim()) return;
      if (key === "cities" && !CONNECTION_CITY_FILTER_SET.has(value)) return;
      setAppliedFilters((prev) => {
        const newFilters = { ...prev };
        if (!newFilters[key].includes(value)) {
          newFilters[key] = [...newFilters[key], value];
        }
        return newFilters;
      });
    },
    []
  );

  const removeFilter = useCallback(
    (key: keyof typeof appliedFilters, index: number) => {
      setAppliedFilters((prev) => ({
        ...prev,
        [key]: prev[key].filter((_, i) => i !== index),
      }));
    },
    []
  );

  const clearAllFilters = useCallback(() => {
    setAppliedFilters({
      cities: [],
      sectors: [],
      turnovers: [],
      countries: [],
    });
  }, []);

  const labelForSectorValue = useCallback(
    (value: string) => {
      const opt = SECTOR_FILTER_OPTIONS.find((o) => o.value === value);
      return opt ? t(opt.labelKey) : value;
    },
    [t]
  );

  const labelForTurnoverValue = useCallback(
    (value: string) => {
      const opt = TURNOVER_FILTER_OPTIONS.find((o) => o.value === value);
      return opt ? t(opt.labelKey) : value;
    },
    [t]
  );

  const hasActiveFilters = useMemo(
    () =>
      appliedFilters.cities.length > 0 ||
      appliedFilters.sectors.length > 0 ||
      appliedFilters.turnovers.length > 0 ||
      appliedFilters.countries.length > 0,
    [
      appliedFilters.cities.length,
      appliedFilters.sectors.length,
      appliedFilters.turnovers.length,
      appliedFilters.countries.length,
    ]
  );

  // Clear restricted filters when membership changes
  useEffect(() => {
    if (membershipType === "youth") {
      clearAllFilters();
    } else if (membershipType === "business") {
      setAppliedFilters((prev) => ({
        ...prev,
        turnovers: [],
        countries: [],
      }));
    }
  }, [membershipType, clearAllFilters]);

  /* ================= HELPER FUNCTIONS ================= */

  const mergeImages = useCallback((items: User[]) => {
    if (!items) return [];
    return items.map((u) => ({
      ...u,
      ...fullUsersImages.find((i) => i.userId === u.id)?.imageUrls,
    }));
  }, [fullUsersImages]);

  const sortUsers = useCallback((items: User[] = []) => {
    if (!items) return [];
    return [...items].sort((a, b) => {
      if (sortOption === "connections.sort.firstName") {
        const aName = (a.firstName || "").trim();
        const bName = (b.firstName || "").trim();
        return aName.localeCompare(bName);
      } else if (sortOption === "connections.sort.lastName") {
        const aName = (a.lastName || "").trim();
        const bName = (b.lastName || "").trim();
        return aName.localeCompare(bName);
      }
      return 0;
    });
  }, [sortOption]);

  const filterExcludedUsers = useCallback((items: User[] = []) => {
    if (!items) return [];
    return items.filter(
      (u, i, self) =>
        i === self.findIndex((x) => x.objectId === u.objectId) &&
        u.objectId !== userId &&
        !connections?.userDtos?.some((c) => c.objectId === u.objectId) &&
        !connectionRequests?.userDtos?.some((r) => r.objectId === u.objectId)
    );
  }, [userId, connections?.userDtos, connectionRequests?.userDtos]);

  const users = useMemo(() => {
    let list = filterExcludedUsers(sortUsers(mergeImages(fullUsersList)));
    const cityF =
      canUseCityFilter && appliedFilters.cities.length > 0
        ? appliedFilters.cities.filter((c) => CONNECTION_CITY_FILTER_SET.has(c))
        : [];
    const sectorF =
      canUseSectorFilter && appliedFilters.sectors.length > 0 ? appliedFilters.sectors : [];
    const turnF =
      canUseTurnoverFilter && appliedFilters.turnovers.length > 0 ? appliedFilters.turnovers : [];
    const countryF =
      canUseCountryFilter && appliedFilters.countries.length > 0 ? appliedFilters.countries : [];
    if (cityF.length > 0 || sectorF.length > 0 || turnF.length > 0 || countryF.length > 0) {
      list = list.filter((u) =>
        userMatchesDiscoverFilters(u, {
          cities: cityF,
          sectors: sectorF,
          turnovers: turnF,
          countries: countryF,
        })
      );
    }
    return list;
  }, [
    fullUsersList,
    mergeImages,
    sortUsers,
    filterExcludedUsers,
    canUseCityFilter,
    canUseSectorFilter,
    canUseTurnoverFilter,
    canUseCountryFilter,
    appliedFilters.cities,
    appliedFilters.sectors,
    appliedFilters.turnovers,
    appliedFilters.countries,
  ]);

  const connectionsListData = useMemo(
    () => mergeImages(sortUsers(connections?.userDtos ?? [])),
    [connections?.userDtos, mergeImages, sortUsers]
  );

  const requestsListData = useMemo(
    () => mergeImages(sortUsers(connectionRequests?.userDtos ?? [])),
    [connectionRequests?.userDtos, mergeImages, sortUsers]
  );

  const sentRequestsListData = useMemo(
    () => mergeImages(sortUsers(sentConnectionRequests?.userDtos ?? [])),
    [sentConnectionRequests?.userDtos, mergeImages, sortUsers]
  );

  const filterUsersBySearch = useCallback((items: User[] = [], term: string) => {
    if (!items.length || !term.trim()) return items;
    const q = term.trim().toLowerCase();
    return items.filter((u) => {
      const first = (u.firstName || "").toLowerCase();
      const last = (u.lastName || "").toLowerCase();
      const full = `${first} ${last}`.trim();
      return first.includes(q) || last.includes(q) || full.includes(q);
    });
  }, []);

  const filteredConnectionsListData = useMemo(
    () => filterUsersBySearch(connectionsListData, searchQuery),
    [connectionsListData, searchQuery, filterUsersBySearch]
  );

  const filteredRequestsListData = useMemo(
    () => filterUsersBySearch(requestsListData, searchQuery),
    [requestsListData, searchQuery, filterUsersBySearch]
  );

  const filteredSentRequestsListData = useMemo(
    () => filterUsersBySearch(sentRequestsListData, searchQuery),
    [sentRequestsListData, searchQuery, filterUsersBySearch]
  );

  const loadMoreUsers = useCallback(
    () => setCurrentPage((p) => p + 1),
    []
  );

  const handleOpenFilters = useCallback(() => setShowFilters(true), []);
  const handleCloseFilters = useCallback(() => setShowFilters(false), []);
  const handleTabSelect = useCallback(
    (k: string | null) => k && setActiveTab(k as "connections" | "requests" | "users"),
    []
  );
  const handleSortSelect = useCallback((k: string | null) => {
    if (k) setSortOption(k);
  }, []);

  /* ================= UI ================= */

  return (
    <div className="connections-root p-3 p-md-4 position-relative pt-5 pt-md-5 d-flex flex-column min-vh-100 vh-100 overflow-hidden min-vw-0">
      {/* Header: sticky on mobile (via CSS), fixed at top on desktop (scroll area below) */}
      <div className="connections-header-sticky bg-white pb-2 flex-shrink-0">
      {/* Desktop: Header Row - fixed min-height to prevent layout shift when switching tabs */}
      <div className="d-none d-md-flex flex-row justify-content-between align-items-center mb-3 connections-desktop-header-row">
        <h5 className="fw-bold mb-0 text-dark">{t("connections.title")}</h5>
        {activeTab === "users" && (
          <Button
            variant="outline-primary"
            className="d-flex align-items-center gap-2 position-relative rounded-2 shadow-none px-2"
            style={{ paddingTop: 6, paddingBottom: 6 }}
            onClick={handleOpenFilters}
            aria-label={t("connections.filter")}
          >
            <BsFilter size={16} aria-hidden />
            <span>{t("connections.filter")}</span>
            {hasActiveFilters && (
              <Badge bg="primary" pill className="position-absolute top-0 start-100 translate-middle">
                {appliedFilters.cities.length + appliedFilters.sectors.length + appliedFilters.turnovers.length + appliedFilters.countries.length}
              </Badge>
            )}
          </Button>
        )}
      </div>

      {/* Mobile: Subtitle row + title – keep equal height across tabs */}
      <div className="d-md-none mb-3">
        {activeTab === "users" && (
          <div className="d-flex flex-row justify-content-between align-items-baseline w-100 mb-1 gap-2 min-w-0">
            <span className="text-muted small text-nowrap connections-mobile-subtitle invisible">{t("connections.incomingRequests")}</span>
            <span className="text-muted text-end small text-nowrap connections-mobile-subtitle invisible">
              {t("connections.acceptToAdd")}
            </span>
          </div>
        )}
        {activeTab === "requests" && (
          <div className="d-flex flex-row justify-content-between align-items-baseline w-100 mb-1 gap-2 min-w-0">
            <span className="text-muted small text-nowrap connections-mobile-subtitle">{t("connections.incomingRequests")}</span>
            <span className="text-muted text-end small text-nowrap connections-mobile-subtitle">{t("connections.acceptToAdd")}</span>
          </div>
        )}
        {activeTab === "connections" && (
          <div className="d-flex flex-row justify-content-between align-items-baseline w-100 mb-1">
            <span className="text-muted small connections-mobile-subtitle">{t("connections.yourConnections")}</span>
            <span className="text-muted text-end small connections-mobile-subtitle">{t("connections.connectedCount", { count: filteredConnectionsListData.length })}</span>
          </div>
        )}
        <h5 className="fw-bold mb-0 text-dark">{t("connections.title")}</h5>
      </div>

      {/* FILTER SIDEBAR / MODAL: offcanvas on desktop, full-screen modal on mobile */}
      <Offcanvas
        show={showFilters}
        onHide={handleCloseFilters}
        placement="end"
      >
        <Offcanvas.Header closeButton>
          <Offcanvas.Title>{t("connections.filtersTitle")}</Offcanvas.Title>
        </Offcanvas.Header>

        <Offcanvas.Body>
          {/* City Filter */}
          <div className="mb-3">
            <label className="form-label small fw-bold">{t("connections.city")}</label>
            <Form.Select
              size="sm"
              value={cityFilter}
              onChange={(e) => {
                const value = e.target.value;
                setCityFilter(value);
                if (value && canUseCityFilter) {
                  addFilter("cities", value);
                  setCityFilter("");
                }
              }}
              disabled={!canUseCityFilter || cityOptions.length === 0}
            >
              <option value="">
                {cityOptions.length === 0 ? t("connections.noCitiesAvailable") : t("connections.selectCity")}
              </option>
              {cityOptions.map((city) => (
                <option key={city} value={city}>
                  {displayCity(city)}
                </option>
              ))}
            </Form.Select>
            {!canUseCityFilter && (
              <div className="text-muted small mt-1">
                {t(getUpgradeMessageKey(membershipType, "city"))}
              </div>
            )}
            {appliedFilters.cities.length > 0 && (
              <div className="d-flex flex-wrap gap-2 mt-2">
                {appliedFilters.cities.map((city, i) => (
                  <span key={i} className="badge bg-primary">
                    {displayCity(city)}
                    <button
                      type="button"
                      className="btn-close btn-close-white ms-2"
                      onClick={() => removeFilter("cities", i)}
                      aria-label={t("connections.remove")}
                    />
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Sector Filter */}
          <div className="mb-3">
            <label className="form-label small fw-bold">{t("connections.sectorIndustry")}</label>
            <Form.Select
              size="sm"
              value={sectorFilter}
              onChange={(e) => {
                if (e.target.value && canUseSectorFilter) {
                  addFilter("sectors", e.target.value);
                  setSectorFilter("");
                }
              }}
              disabled={!canUseSectorFilter}
            >
              <option value="">{t("connections.selectSector")}</option>
              {SECTOR_FILTER_OPTIONS.map((s) => (
                <option key={s.value} value={s.value}>
                  {t(s.labelKey)}
                </option>
              ))}
            </Form.Select>
            {!canUseSectorFilter && (
              <div className="text-muted small mt-1">
                {t(getUpgradeMessageKey(membershipType, "sector"))}
              </div>
            )}
            {appliedFilters.sectors.length > 0 && (
              <div className="d-flex flex-wrap gap-2 mt-2">
                {appliedFilters.sectors.map((sector, i) => (
                  <span key={i} className="badge bg-primary">
                    {labelForSectorValue(sector)}
                    <button
                      type="button"
                      className="btn-close btn-close-white ms-2"
                      onClick={() => removeFilter("sectors", i)}
                      aria-label={t("connections.remove")}
                    />
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Turnover Filter */}
          <div className="mb-3">
            <label className="form-label small fw-bold">{t("connections.businessTurnover")}</label>
            <Form.Select
              size="sm"
              value={turnoverFilter}
              onChange={(e) => {
                if (e.target.value && canUseTurnoverFilter) {
                  addFilter("turnovers", e.target.value);
                  setTurnoverFilter("");
                }
              }}
              disabled={!canUseTurnoverFilter}
            >
              <option value="">{t("connections.selectTurnover")}</option>
              {TURNOVER_FILTER_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {t(opt.labelKey)}
                </option>
              ))}
            </Form.Select>
            {!canUseTurnoverFilter && (
              <div className="text-muted small mt-1">
                {t(getUpgradeMessageKey(membershipType, "turnover"))}
              </div>
            )}
            {appliedFilters.turnovers.length > 0 && (
              <div className="d-flex flex-wrap gap-2 mt-2">
                {appliedFilters.turnovers.map((turnover, i) => (
                  <span key={i} className="badge bg-primary">
                    {labelForTurnoverValue(turnover)}
                    <button
                      type="button"
                      className="btn-close btn-close-white ms-2"
                      onClick={() => removeFilter("turnovers", i)}
                      aria-label={t("connections.remove")}
                    />
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Country Filter */}
          <div className="mb-3">
            <label className="form-label small fw-bold">{t("connections.country")}</label>
            <Form.Select
              size="sm"
              value={countryFilter}
              onChange={(e) => {
                if (e.target.value && canUseCountryFilter) {
                  addFilter("countries", e.target.value);
                  setCountryFilter("");
                }
              }}
              disabled={!canUseCountryFilter}
            >
              <option value="">{t("connections.selectCountry")}</option>
              {COUNTRY_OPTIONS.map((c, i) => (
                <option key={i} value={c}>
                  {formatCountryForLocale(c, locale)}
                </option>
              ))}
            </Form.Select>
            {!canUseCountryFilter && (
              <div className="text-muted small mt-1">
                {t(getUpgradeMessageKey(membershipType, "country"))}
              </div>
            )}
            {appliedFilters.countries.length > 0 && (
              <div className="d-flex flex-wrap gap-2 mt-2">
                {appliedFilters.countries.map((country, i) => (
                  <span key={i} className="badge bg-primary d-inline-flex align-items-center gap-1">
                    {getCountryFlagUrl(country) && (
                      <img
                        src={getCountryFlagUrl(country)!}
                        alt=""
                        width={24}
                        height={18}
                        className="rounded object-fit-cover"
                      />
                    )}
                    {formatCountryForLocale(country, locale)}
                    <button
                      type="button"
                      className="btn-close btn-close-white ms-2"
                      onClick={() => removeFilter("countries", i)}
                      aria-label={t("connections.remove")}
                    />
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Clear All Filters */}
          {hasActiveFilters && (
            <Button
              variant="outline-danger"
              size="sm"
              className="w-100 mb-3"
              onClick={clearAllFilters}
            >
              {t("connections.clearAllFilters")}
            </Button>
          )}

          <Button
            variant="primary"
            className="w-100"
            onClick={handleCloseFilters}
          >
            {t("connections.applyFilters")}
          </Button>
        </Offcanvas.Body>
      </Offcanvas>

      {/* Tablet (768–991px): stacked – tabs full width, then search+sort. Desktop (992px+): tabs left, search+sort right */}
      <div className="d-none d-md-flex flex-column flex-lg-row flex-nowrap align-items-end mb-1 gap-2 connections-stable-order">
        {/* Tabs: full width on tablet only */}
        <div className="connections-tabs-tablet-full bg-light rounded-2 border shadow-sm d-flex p-1 flex-shrink-0">
          <Nav
            activeKey={activeTab}
            onSelect={handleTabSelect}
            className="d-flex flex-nowrap gap-2"
          >
            <Nav.Item>
              <Nav.Link
                eventKey="users"
                className={`d-flex align-items-center gap-2 rounded-2 text-dark text-decoration-none border-0 px-2 py-1 ${activeTab === "users" ? "shadow bg-white" : "bg-transparent"}`}
                title={t("connections.discoverTabTitle")}
              >
                <BsSearch size={16} className={`flex-shrink-0 ${activeTab === "users" ? "text-primary" : "text-dark"}`} aria-hidden />
                <span className="d-flex flex-column align-items-start text-start small lh-sm">
                  <span className={activeTab === "users" ? "text-primary fw-bold" : "text-dark fw-normal"}>{t("connections.discover")}</span>
                  <span className="text-muted small">{t("connections.discoverPeople")}</span>
                </span>
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link
                eventKey="requests"
                className={`d-flex align-items-center gap-2 rounded-2 text-dark text-decoration-none border-0 px-2 py-1 ${activeTab === "requests" ? "shadow bg-white" : "bg-transparent"}`}
                title={t("connections.pendingRequestsTitle")}
              >
                <BsEnvelopeArrowDown size={16} className={`flex-shrink-0 ${activeTab === "requests" ? "text-primary" : "text-dark"}`} aria-hidden />
                <span className="d-flex flex-column align-items-start text-start small lh-sm">
                  <span className={`d-inline-flex align-items-center gap-1 ${activeTab === "requests" ? "text-primary fw-bold" : "text-dark fw-normal"}`}>
                    {t("connections.requests")}
                    {requestsListData.length > 0 && (
                      <Badge bg="primary" pill className="small px-2">{requestsListData.length}</Badge>
                    )}
                  </span>
                  <span className="text-muted small">{t("connections.pendingInvites")}</span>
                </span>
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link
                eventKey="connections"
                className={`d-flex align-items-center gap-2 rounded-2 text-dark text-decoration-none border-0 px-2 py-1 ${activeTab === "connections" ? "shadow bg-white" : "bg-transparent"}`}
                title={t("connections.peopleConnectedWith")}
              >
                <BsPeople size={16} className={`flex-shrink-0 ${activeTab === "connections" ? "text-primary" : "text-dark"}`} aria-hidden />
                <span className="d-flex flex-column align-items-start text-start small lh-sm">
                  <span className={activeTab === "connections" ? "text-primary fw-bold" : "text-dark fw-normal"}>{t("connections.myNetwork")}</span>
                  <span className="text-muted small">{t("connections.yourNetwork")}</span>
                </span>
              </Nav.Link>
            </Nav.Item>
          </Nav>
        </div>

        {/* Search + Sort: full width on tablet, grows on desktop */}
        <div className="d-flex align-items-center gap-1 flex-grow-1 justify-content-end w-100 min-w-0" style={{ minWidth: '300px', maxWidth: '100%' }}>
          <InputGroup className="rounded-2 flex-grow-1 connections-search-desktop">
            <InputGroup.Text className="bg-white border border-end-0 rounded-start d-flex align-items-center px-1 px-md-2">
              <BsSearch size={16} className="text-secondary" aria-hidden />
            </InputGroup.Text>
            <Form.Control
              ref={searchInputRef}
              lang={connectionsSearchLiveLang}
              placeholder={t("connections.searchPlaceholder")}
              value={searchDisplay}
              onChange={handleConnectionsSearchChange}
              onBlur={handleConnectionsSearchBlur}
              aria-busy={isConnectionsSearchTranslating}
              aria-label={t("connections.search")}
              className="border border-start-0 rounded-end bg-white shadow-none"
              style={{ paddingTop: 6, paddingBottom: 6 }}
            />
          </InputGroup>
          <Dropdown onSelect={handleSortSelect}>
            <Dropdown.Toggle
              variant="outline-primary"
              className="d-flex align-items-center gap-2 rounded-2 shadow-none flex-shrink-0 px-2"
              style={{ paddingTop: 6, paddingBottom: 6 }}
              aria-label={t("connections.sortBy")}
            >
              <BsSortDown size={16} aria-hidden />
              <span>{t("connections.sort")}</span>
            </Dropdown.Toggle>
            <Dropdown.Menu>
              <Dropdown.Item eventKey="connections.sort.recent">{t("connections.sort.recent")}</Dropdown.Item>
              <Dropdown.Item eventKey="connections.sort.firstName">{t("connections.sort.firstName")}</Dropdown.Item>
              <Dropdown.Item eventKey="connections.sort.lastName">{t("connections.sort.lastName")}</Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </div>
      </div>

      {/* Mobile: Navigation Tabs – compact size, rounded container, icon above text */}
      <nav className="d-sm-none mb-2 position-relative" aria-label={t("connections.title")}>
        <div className="bg-light rounded-2 border shadow-sm d-flex p-1 overflow-x-auto">
          <button
            type="button"
            className={`d-flex flex-column align-items-center justify-content-center gap-1 border-0 flex-fill py-1 px-0 rounded-1 ${activeTab === "users" ? "bg-primary text-white fw-semibold" : "bg-white text-dark"}`}
            onClick={() => setActiveTab("users")}
            aria-label={t("connections.discoverMobileAria")}
          >
            <BsSearch size={14} aria-hidden />
            <span className="small">{t("connections.discover")}</span>
          </button>
          <button
            type="button"
            className={`d-flex flex-column align-items-center justify-content-center gap-1 border-0 flex-fill position-relative py-1 px-0 rounded-1 ${activeTab === "requests" ? "bg-primary text-white fw-semibold" : "bg-white text-dark"}`}
            onClick={() => setActiveTab("requests")}
            aria-label={t("connections.requestsMobileAria")}
          >
            <BsEnvelopeArrowDown size={14} aria-hidden />
            <span className="small">{t("connections.requests")}</span>
          </button>
          <button
            type="button"
            className={`d-flex flex-column align-items-center justify-content-center gap-1 border-0 flex-fill py-1 px-0 rounded-1 ${activeTab === "connections" ? "bg-primary text-white fw-semibold" : "bg-white text-dark"}`}
            onClick={() => setActiveTab("connections")}
            aria-label={t("connections.networkMobileAria")}
          >
            <BsPeople size={14} aria-hidden />
            <span className="small">{t("connections.myNetwork")}</span>
          </button>
        </div>
      </nav>

      {/* Mobile: Search bar – full placeholder visible like reference image */}
      <div className="d-flex d-md-none flex-column gap-2 mb-0">
        <InputGroup className="rounded-3 border shadow-sm w-100 bg-white min-w-0">
          <Form.Control
            ref={searchInputRef}
            lang={connectionsSearchLiveLang}
            placeholder={t("connections.searchPlaceholder")}
            value={searchDisplay}
            onChange={handleConnectionsSearchChange}
            onBlur={handleConnectionsSearchBlur}
            aria-busy={isConnectionsSearchTranslating}
            aria-label={t("connections.searchPlaceholder")}
            className="border-0 border-end rounded-start bg-white shadow-none py-2 px-3 flex-grow-1 min-w-0 connections-search-placeholder"
          />
          <InputGroup.Text className="bg-white border-0 border-start rounded-end d-flex align-items-center px-2 py-2 flex-shrink-0">
            <BsSearch size={16} className="text-secondary" aria-hidden />
          </InputGroup.Text>
        </InputGroup>
      </div>
      </div>
      {/* END FIXED – scrollable section below */}

      <div className="connections-scroll-area d-flex flex-column pt-2 pt-md-0 overflow-y-auto flex-grow-1 min-h-0">
        {activeTab === "users" && (
          <div className="d-flex d-md-none align-items-stretch justify-content-between gap-2 w-100 mb-2 connections-stable-order">
            <Button
              variant="outline-primary"
              className="d-flex align-items-center gap-2 position-relative rounded-2 text-nowrap shadow-none py-2 px-3"
              onClick={handleOpenFilters}
              aria-label={t("connections.filter")}
            >
              <BsFilter size={16} aria-hidden />
              <span>{t("connections.filters")}</span>
              {hasActiveFilters && (
                <Badge bg="primary" pill className="position-absolute top-0 start-100 translate-middle">
                  {appliedFilters.cities.length +
                    appliedFilters.sectors.length +
                    appliedFilters.turnovers.length +
                    appliedFilters.countries.length}
                </Badge>
              )}
            </Button>
            <Dropdown onSelect={handleSortSelect}>
              <Dropdown.Toggle
                variant="outline-primary"
                className="d-flex align-items-center gap-2 rounded-2 shadow-none py-2 px-3"
                aria-label={t("connections.sort")}
              >
                <BsSortDown size={16} aria-hidden />
                <span>{t("connections.sort")}</span>
              </Dropdown.Toggle>
              <Dropdown.Menu>
                <Dropdown.Item eventKey="connections.sort.recent">{t("connections.sort.recent")}</Dropdown.Item>
                <Dropdown.Item eventKey="connections.sort.firstName">{t("connections.sort.firstName")}</Dropdown.Item>
                <Dropdown.Item eventKey="connections.sort.lastName">{t("connections.sort.lastName")}</Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </div>
        )}

        {(activeTab === "requests" || activeTab === "connections") && (
          <div className="d-flex d-md-none justify-content-end mb-2 connections-stable-order">
            <Dropdown onSelect={handleSortSelect}>
              <Dropdown.Toggle
                variant="outline-primary"
                className="d-flex align-items-center gap-2 rounded-2 shadow-none py-2 px-3"
                aria-label={t("connections.sort")}
              >
                <BsSortDown size={16} aria-hidden />
                <span>{t("connections.sort")}</span>
              </Dropdown.Toggle>
              <Dropdown.Menu>
                <Dropdown.Item eventKey="connections.sort.recent">{t("connections.sort.recent")}</Dropdown.Item>
                <Dropdown.Item eventKey="connections.sort.firstName">{t("connections.sort.firstName")}</Dropdown.Item>
                <Dropdown.Item eventKey="connections.sort.lastName">{t("connections.sort.lastName")}</Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </div>
        )}

      {/* Subtitle row: same structure on all tabs so cards start at same position (Bootstrap invisible on Discover) */}
      <div className="d-none d-sm-flex justify-content-between align-items-center mb-1 gap-2 min-w-0">
        {activeTab === "users" && (
          <>
            <h6 className="mb-0 fw-normal text-nowrap connections-mobile-subtitle invisible">{t("connections.incomingRequests")}</h6>
            <span className="text-muted small mb-0 mb-md-2 mt-0 text-nowrap text-end connections-mobile-subtitle invisible">{t("connections.acceptToAdd")}</span>
          </>
        )}
        {activeTab === "requests" && (
          <>
            <h6 className="mb-0 fw-normal text-nowrap connections-mobile-subtitle">{t("connections.incomingRequests")}</h6>
            <span className="text-muted small mb-0 mb-md-2 mt-0 text-nowrap text-end connections-mobile-subtitle">{t("connections.acceptToAdd")}</span>
          </>
        )}
        {activeTab === "connections" && (
          <>
            <h6 className="mb-0 fw-normal connections-mobile-subtitle">{t("connections.yourConnections")}</h6>
            <span className="text-muted small mb-0 mb-md-2 mt-0 connections-mobile-subtitle connections-subtitle">
              {t("connections.connectedCount", { count: filteredConnectionsListData.length })}
            </span>
          </>
        )}
      </div>

      <div className="mt-1 mt-md-2 pb-5 pb-md-0">
        {activeTab === "users" && (
          <Suspense fallback={<div className="min-vh-50" aria-hidden />}>
            <UsersList
              users={users}
              loadMoreUsers={loadMoreUsers}
              hasMoreUsers={hasMoreUsers}
            />
          </Suspense>
        )}

        {activeTab === "connections" && (
          <Suspense fallback={<div className="min-vh-50" aria-hidden />}>
            <ConnectionsList
              connections={filteredConnectionsListData}
              refetchConnections={refetchConnections}
            />
          </Suspense>
        )}

        {activeTab === "requests" && (
          <Suspense fallback={<div className="min-vh-50" aria-hidden />}>
            <RequestsList
              requests={filteredRequestsListData}
              sentRequests={filteredSentRequestsListData}
              refetchConnections={refetchConnections}
              refetchConnectionRequests={refetchConnectionRequests}
              refetchSentConnectionRequests={refetchSentConnectionRequests}
            />
          </Suspense>
        )}
      </div>
      </div>
    </div>
  );
};

export default Connections;